import React from 'react';
import Style from './index.less';
import classNames from 'classnames';
import * as ICON from './preset-icons';
import {
    OuterShadowPresetStyleList,
    InnerShadowPresetStyleList,
    PerspectiveShadowPresetStyleList,
    DefaultEffectPresetProps,
    EffectPropsField,
} from '@src/framework/editor/src/js/constants';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import i18n from '@i18next/i18n';
import * as TextIcon from '@src/framework/frame-pc/components/palette-panel/effect-preset-menu/shadow-preset-menu/preset-icons/index-text';
import ImageQuickLookManager
    from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import { EEffectAlgn } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';

const OuterShadowPresetProps = {
    ...DefaultEffectPresetProps[ActionType.SHADOW],
    [EffectPropsField.ANGLE.name]: 0,
};
const InnerShadowPresetProps = {
    ...DefaultEffectPresetProps[ActionType.SHADOW],
    [EffectPropsField.OPACITY.name]: 50,
    [EffectPropsField.ANGLE.name]: 0,
    isOuter: false,
};
export const ShadowStyleAndPropsMapping = {
    'shadow-empty-click': null,
    'shadow-empty-hover': {
        [EffectPropsField.COLOR.name]: '#000000',
        [EffectPropsField.TD_COLOR.name]: new TDColor({ rgbaStr: '#000000' }),
        [EffectPropsField.OPACITY.name]: 0,
        [EffectPropsField.SIZE.name]: 0,
        [EffectPropsField.BLURRINESS.name]: 0,
        [EffectPropsField.ANGLE.name]: 0,
        [EffectPropsField.DISTANCE.name]: 0,
        isOuter: true,
    },
    'outer-45': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 45, [EffectPropsField.DISTANCE.name]: 4, [EffectPropsField.ALGN.name]: EEffectAlgn.TL },
    'outer-90': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 90, [EffectPropsField.DISTANCE.name]: 4, [EffectPropsField.ALGN.name]: EEffectAlgn.T },
    'outer-135': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 135, [EffectPropsField.DISTANCE.name]: 4, [EffectPropsField.ALGN.name]: EEffectAlgn.TR },
    'outer-0': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 0, [EffectPropsField.DISTANCE.name]: 4, [EffectPropsField.ALGN.name]: EEffectAlgn.L },
    'outer-0-phai': { ...OuterShadowPresetProps, [EffectPropsField.SIZE.name]: 102, [EffectPropsField.ALGN.name]: EEffectAlgn.CTR, [EffectPropsField.BLURRINESS.name]: 5, [EffectPropsField.DISTANCE.name]: 0 },
    'outer-180': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 180, [EffectPropsField.DISTANCE.name]: 4, [EffectPropsField.ALGN.name]: EEffectAlgn.R },
    'outer-315': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 315, [EffectPropsField.DISTANCE.name]: 4, [EffectPropsField.ALGN.name]: EEffectAlgn.BL },
    'outer-270': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 270, [EffectPropsField.DISTANCE.name]: 4, [EffectPropsField.ALGN.name]: EEffectAlgn.B },
    'outer-225': { ...OuterShadowPresetProps, [EffectPropsField.ANGLE.name]: 225, [EffectPropsField.DISTANCE.name]: 4, [EffectPropsField.ALGN.name]: EEffectAlgn.BR },

    'inner-225': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 225, [EffectPropsField.DISTANCE.name]: 4 },
    'inner-270': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 270, [EffectPropsField.DISTANCE.name]: 4 },
    'inner-315': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 315, [EffectPropsField.DISTANCE.name]: 4 },
    'inner-180': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 180, [EffectPropsField.DISTANCE.name]: 4 },
    'inner-0-phai': { ...InnerShadowPresetProps, [EffectPropsField.OPACITY.name]: 0, [EffectPropsField.BLURRINESS.name]: 9, [EffectPropsField.DISTANCE.name]: 0 },
    'inner-0': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 0, [EffectPropsField.DISTANCE.name]: 4 },
    'inner-135': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 135, [EffectPropsField.DISTANCE.name]: 4 },
    'inner-90': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 90, [EffectPropsField.DISTANCE.name]: 4 },
    'inner-45': { ...InnerShadowPresetProps, [EffectPropsField.ANGLE.name]: 45, [EffectPropsField.DISTANCE.name]: 4 },

    'perspective-1': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.CTR,
        [EffectPropsField.ANGLE.name]: 128,
        [EffectPropsField.DISTANCE.name]: 24.41,
        [EffectPropsField.OPACITY.name]: 68,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: 30,
        [EffectPropsField.SKEW_X.name]: 21.67,
    },
    'perspective-2': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.BL,
        [EffectPropsField.ANGLE.name]: 250,
        [EffectPropsField.DISTANCE.name]: 15.75,
        [EffectPropsField.OPACITY.name]: 68,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: 30,
        [EffectPropsField.SKEW_X.name]: -30,
    },
    'perspective-3': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.B,
        [EffectPropsField.ANGLE.name]: 90,
        [EffectPropsField.DISTANCE.name]: 3,
        [EffectPropsField.OPACITY.name]: 75,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: -20,
    },
    'perspective-4': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.BL,
        [EffectPropsField.ANGLE.name]: 25,
        [EffectPropsField.OPACITY.name]: 80,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: -30,
        [EffectPropsField.SKEW_X.name]: 13.34,
    },
    'perspective-5': {
        ...OuterShadowPresetProps,
        [EffectPropsField.ALGN.name]: EEffectAlgn.BL,
        [EffectPropsField.ANGLE.name]: 33.34,
        [EffectPropsField.OPACITY.name]: 80,
        [EffectPropsField.SIZE_X.name]: 100,
        [EffectPropsField.SIZE_Y.name]: -30,
        [EffectPropsField.SKEW_X.name]: -13.34,
    },
};

const TextIconMapping = {
    'inner-0': TextIcon.TextInner0,
    'inner-0-phai': TextIcon.TextInner0Phai,
    'inner-45': TextIcon.TextInner45,
    'inner-90': TextIcon.TextInner90,
    'inner-135': TextIcon.TextInner135,
    'inner-180': TextIcon.TextInner180,
    'inner-225': TextIcon.TextInner225,
    'inner-270': TextIcon.TextInner270,
    'inner-315': TextIcon.TextInner315,
    'outer-0': TextIcon.TextOuter0,
    'outer-0-phai': TextIcon.TextOuter0Phai,
    'outer-45': TextIcon.TextOuter45,
    'outer-90': TextIcon.TextOuter90,
    'outer-135': TextIcon.TextOuter135,
    'outer-180': TextIcon.TextOuter180,
    'outer-225': TextIcon.TextOuter225,
    'outer-270': TextIcon.TextOuter270,
    'outer-315': TextIcon.TextOuter315,
    'perspective-1': TextIcon.TextPerspective1,
    'perspective-2': TextIcon.TextPerspective2,
    'perspective-3': TextIcon.TextPerspective3,
    'perspective-4': TextIcon.TextPerspective4,
    'perspective-5': TextIcon.TextPerspective5,
};

interface ShadowPresetMenuProps {
    preset: {
        style: string;
    };
    onShadowPresetStyleClick: (props: any) => void;
    onShadowPresetStyleHover?: (props: any) => void;
    onShadowPresetStyleMouseEnter: (props: any) => void;
    onShadowPresetStyleMouseLeave: () => void;
    containerStyle?: React.CSSProperties;
}
export default class ShadowPresetMenu extends React.Component<ShadowPresetMenuProps> {
    public static PresetIconOnShow = {
        'shadow-empty': ICON.DrawerEMPTY,

        'outer-45': ICON.DrawerOuter45, 'outer-90': ICON.DrawerOuter90, 'outer-135': ICON.DrawerOuter135,
        'outer-0': ICON.DrawerOuter0, 'outer-0-phai': ICON.DrawerOuter0Phai, 'outer-180': ICON.DrawerOuter180,
        'outer-225': ICON.DrawerOuter225, 'outer-270': ICON.DrawerOuter270, 'outer-315': ICON.DrawerOuter315,

        'inner-45': ICON.DrawerInner45, 'inner-90': ICON.DrawerInner90, 'inner-135': ICON.DrawerInner135,
        'inner-0': ICON.DrawerInner0, 'inner-0-phai': ICON.DrawerInner0Phai, 'inner-180': ICON.DrawerInner180,
        'inner-225': ICON.DrawerInner225, 'inner-270': ICON.DrawerInner270, 'inner-315': ICON.DrawerInner315,

        'perspective-1': ICON.DrawerPerspective1, 'perspective-2': ICON.DrawerPerspective2, 'perspective-3': ICON.DrawerPerspective3,
        'perspective-4': ICON.DrawerPerspective4, 'perspective-5': ICON.DrawerPerspective5,
    };

    private outerShadowPresetDropList: { style: string; key: string; isSelected: boolean }[] = [];
    private innerShadowPresetDropList: { style: string; key: string; isSelected: boolean }[] = [];
    private perspectiveShadowPresetDropList: { style: string; key: string; isSelected: boolean }[] = [];

    public constructor(props: ShadowPresetMenuProps, context: any) {
        super(props, context);

        for (let i = 0, len = OuterShadowPresetStyleList.length; i < len; i++) {
            const item = OuterShadowPresetStyleList[i];
            this.outerShadowPresetDropList.push({
                ...item,
                isSelected: item.style === this.props.preset.style,
            });
        }
        for (let j = 0, len = InnerShadowPresetStyleList.length; j < len; j++) {
            const item = InnerShadowPresetStyleList[j];
            this.innerShadowPresetDropList.push({
                ...item,
                isSelected: item.style === this.props.preset.style,
            });
        }
        for (let k = 0, len = PerspectiveShadowPresetStyleList.length; k < len; k++) {
            const item = PerspectiveShadowPresetStyleList[k];
            this.perspectiveShadowPresetDropList.push({
                ...item,
                isSelected: item.style === this.props.preset.style,
            });
        }
    }

    public render() {
        return (
            <div
                className={classNames([Style.container, Style['container-text']])}
                style={{
                    ...this.props.containerStyle,
                }}
            >
                <div className={Style['no-shadow-title']}
                    onClick={() => this.handleShadowPresetIconClick('shadow-empty-click')}
                    onMouseEnter={() => this.handleShadowPresetIconMouseEnter('shadow-empty-hover')}
                    onMouseLeave={() => this.handleShadowPresetIconMouseLeave()}
                >
                    <span>{i18n.t('无效果')}</span>
                </div>
                <div className={Style['shadow-type-title']}>{i18n.t('外阴影')}</div>
                <div className={Style['shadow-collection']}>
                    {
                        this.outerShadowPresetDropList.map((item) => {
                            const shadowItemClass = classNames({
                                [Style['shadow-collection-item']]: true,
                                [Style['shadow-item-selected']]: item.isSelected,
                            });
                            return (
                                <div key={item.key}
                                    className={shadowItemClass}
                                    onMouseEnter={() => {
                                        this.handleShadowPresetIconMouseEnter(item.style);
                                    }}
                                    onClick={() => {
                                        this.handleShadowPresetIconClick(item.style);
                                    }}
                                    onMouseLeave={() => {
                                        this.handleShadowPresetIconMouseLeave();
                                    }}>
                                    {/* <img src={item.icon}></img> */}
                                    <div className={Style['shadow-item-img']}>
                                        <img src={ImageQuickLookManager.getInstance().getDisplayUrl(TextIconMapping[item.style])} style={{ width: '25px', height: 'auto' }}/>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
                <div className={Style['shadow-type-title']}>{i18n.t('内阴影')}</div>
                <div className={Style['shadow-collection']}>
                    {
                        this.innerShadowPresetDropList.map((item) => {
                            const shadowItemClass = classNames({
                                [Style['shadow-collection-item']]: true,
                                [Style['shadow-item-selected']]: item.isSelected,
                            });
                            return (
                                <div key={item.key}
                                    className={shadowItemClass}
                                    onMouseEnter={() => {
                                        this.handleShadowPresetIconMouseEnter(item.style);
                                    }}
                                    onClick={() => {
                                        this.handleShadowPresetIconClick(item.style);
                                    }}
                                    onMouseLeave={() => {
                                        this.handleShadowPresetIconMouseLeave();
                                    }}>
                                    {/* <img src={item.icon}></img> */}
                                    <div className={Style['shadow-item-img']}>
                                        <img src={ImageQuickLookManager.getInstance().getDisplayUrl(TextIconMapping[item.style])} style={{ width: '23px', height: 'auto' }}/>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
                <div className={Style['shadow-type-title']}>{i18n.t('透视')}</div>
                <div className={Style['shadow-collection']}>
                    {
                        this.perspectiveShadowPresetDropList.map((item, index) => {
                            const shadowItemClass = classNames({
                                [Style['shadow-collection-item']]: true,
                                [Style['shadow-item-selected']]: item.isSelected,
                            });
                            return (
                                <div key={item.key}
                                    className={shadowItemClass}
                                    onMouseEnter={() => {
                                        this.handleShadowPresetIconMouseEnter(item.style);
                                    }}
                                    onClick={() => {
                                        this.handleShadowPresetIconClick(item.style);
                                    }}
                                    onMouseLeave={() => {
                                        this.handleShadowPresetIconMouseLeave();
                                    }}>
                                    <div className={Style['shadow-item-img']}>
                                        <img
                                            src={ImageQuickLookManager
                                                .getInstance()
                                                .getDisplayUrl(TextIconMapping[item.style])}
                                            style={{
                                                width: ({ 0: '30px', 1: '32px' })[index] || '28px',
                                                height: 'auto',
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        );
    }

    private handleShadowPresetIconMouseEnter = (style: string) => {
        const value = ShadowStyleAndPropsMapping[style];
        this.props.onShadowPresetStyleMouseEnter(value ? { ...value } : value);
    };

    private handleShadowPresetIconClick = (style: string) => {
        const value = ShadowStyleAndPropsMapping[style];
        this.props.onShadowPresetStyleClick(value ? { ...value } : value);
    };

    private handleShadowPresetIconMouseLeave = () => {
        this.props.onShadowPresetStyleMouseLeave();
    };
}
